import { useGoogleMaps } from '@shared/hooks/useGoogleMaps';
import { LatLng, LatLngBounds, LatLngLiteral, Map } from '@shared/hooks/useGooglePlaceAPI';
import { milesToMeters } from '@shared/utils/milesToMeters';

const centerAndZoom = (googleMaps: typeof google.maps) => (center: LatLng | LatLngLiteral, miles: number, map: Map) => {
  // Fit map to a specific mile radius around a center point
  const circle = new googleMaps.Circle({
    center,
    radius: milesToMeters(miles),
    fillOpacity: 0,
    fillColor: 'white',
    strokeWeight: 0,
    map,
  });
  if (circle.getBounds()) {
    map.fitBounds(circle.getBounds()!);
    // Remove circle after map is set to proper bounds
    circle.setMap(null);
  }
};

const getCalculatedCenterAndZoom =
  (googleMaps: typeof google.maps) =>
  (markers: ReadonlyArray<LatLngLiteral>): { center: LatLngLiteral; bounds: LatLngBounds } => {
    const minLat = Math.min(...markers.map(marker => marker.lat));
    const maxLat = Math.max(...markers.map(marker => marker.lat));
    const minLng = Math.min(...markers.map(marker => marker.lng));
    const maxLng = Math.max(...markers.map(marker => marker.lng));

    // Calculate the center of the bounding box
    const center: LatLngLiteral = {
      lat: (minLat + maxLat) / 2,
      lng: (minLng + maxLng) / 2,
    };

    // Calculate the bounds
    const bounds = new googleMaps.LatLngBounds(new googleMaps.LatLng(minLat, minLng), new googleMaps.LatLng(maxLat, maxLng));

    return { center, bounds };
  };

const reduceBounds = (googleMaps: typeof google.maps) => (bounds: LatLngBounds, reductionFactor: number) => {
  const northEast = bounds.getNorthEast();
  const southWest = bounds.getSouthWest();

  const latReduction = (northEast.lat() - southWest.lat()) * reductionFactor;
  const lngReduction = (northEast.lng() - southWest.lng()) * reductionFactor;

  const reducedNorthEast = new googleMaps.LatLng(northEast.lat() - latReduction, northEast.lng() - lngReduction);
  const reducedSouthWest = new googleMaps.LatLng(southWest.lat() + latReduction, southWest.lng() + lngReduction);

  const reducedBounds = new googleMaps.LatLngBounds(reducedSouthWest, reducedNorthEast);
  return reducedBounds;
};

const getDistanceInMeters = (lat1: number, lng1: number, lat2: number, lng2: number) => {
  const R = 6371e3;
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLambda = ((lng2 - lng1) * Math.PI) / 180;

  const a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) + Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

const getRadiusFromBounds = (googleMaps: typeof google.maps) => (bounds: LatLngBounds) => {
  const northEast = bounds.getNorthEast();
  const southWest = bounds.getSouthWest();

  const centerLat = (northEast.lat() + southWest.lat()) / 2;
  const centerLng = (northEast.lng() + southWest.lng()) / 2;

  const radius = getDistanceInMeters(centerLat, centerLng, northEast.lat(), northEast.lng());

  const radiusInKm = radius / 1000;

  return { center: new googleMaps.LatLng(centerLat, centerLng), radius: radiusInKm.toFixed(0) };
};

// Helps typescript understand when the functions are available
type GoogleMapsUtils =
  | {
      centerAndZoom: null;
      getCalculatedCenterAndZoom: null;
      reduceBounds: null;
      getDistanceInMeters: null;
      getRadiusFromBounds: null;
    }
  | {
      centerAndZoom: ReturnType<typeof centerAndZoom>;
      getCalculatedCenterAndZoom: ReturnType<typeof getCalculatedCenterAndZoom>;
      reduceBounds: ReturnType<typeof reduceBounds>;
      getDistanceInMeters: typeof getDistanceInMeters;
      getRadiusFromBounds: ReturnType<typeof getRadiusFromBounds>;
    };

export const useGoogleInteractableMap = (): GoogleMapsUtils => {
  const [googleMaps] = useGoogleMaps();
  if (!googleMaps) {
    return {
      centerAndZoom: null,
      getCalculatedCenterAndZoom: null,
      reduceBounds: null,
      getDistanceInMeters: null,
      getRadiusFromBounds: null,
    };
  }
  return {
    centerAndZoom: centerAndZoom(googleMaps),
    getCalculatedCenterAndZoom: getCalculatedCenterAndZoom(googleMaps),
    reduceBounds: reduceBounds(googleMaps),
    getDistanceInMeters,
    getRadiusFromBounds: getRadiusFromBounds(googleMaps),
  };
};
